import { X, SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[]; // first option = "all" sentinel (empty string value)
}

interface TableFilterBarProps {
  filters: FilterConfig[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClearAll?: () => void;
  className?: string;
}

export function TableFilterBar({
  filters,
  values,
  onChange,
  onClearAll,
  className,
}: TableFilterBarProps) {
  const activeFilters = filters.filter(
    f => values[f.key] && values[f.key] !== ''
  );
  const hasActive = activeFilters.length > 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Filter selects row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-xs font-medium text-coal-400">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
        </span>

        {filters.map(filter => (
          <FilterSelect
            key={filter.key}
            filter={filter}
            value={values[filter.key] ?? ''}
            onChange={v => onChange(filter.key, v)}
          />
        ))}

        {hasActive && (
          <button
            onClick={onClearAll}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-coal-400 transition-colors hover:bg-coal-100 hover:text-coal-700"
          >
            <X className="h-3 w-3" />
            Clear all
          </button>
        )}
      </div>

      {/* Active filter chips */}
      {hasActive && (
        <div className="flex flex-wrap gap-1.5">
          {activeFilters.map(filter => {
            const opt = filter.options.find(o => o.value === values[filter.key]);
            return (
              <span
                key={filter.key}
                className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary"
              >
                {filter.label}: {opt?.label ?? values[filter.key]}
                <button
                  onClick={() => onChange(filter.key, '')}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Internal select pill ──────────────────────────────────────────── */
function FilterSelect({
  filter,
  value,
  onChange,
}: {
  filter: FilterConfig;
  value: string;
  onChange: (v: string) => void;
}) {
  const isActive = value !== '';
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={cn(
        'h-8 cursor-pointer rounded-lg border px-2.5 text-xs font-medium transition-all',
        'focus:outline-none focus:ring-2 focus:ring-primary/20',
        isActive
          ? 'border-primary/60 bg-primary/5 text-primary'
          : 'border-coal-200 bg-white text-coal-500 hover:border-coal-300 hover:text-coal-700'
      )}
    >
      {filter.options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
